/**
 * Deliveries API – mirrors the web app's deliveriesApi + routes/deliveries.ts
 * in vin-pulse-os-api.
 *
 * Customer endpoint used here:
 *   GET /api/v1/customers/me/deliveries  – the signed-in customer's own
 *   delivery stops (requireRole('Customer')). This is exactly what the web
 *   app shows customers for order tracking: stop status, scheduled window,
 *   completion time, and proof-of-delivery. No live driver location is
 *   available to customers (the backend returns an empty driver list for the
 *   Customer role), so there is no map here — same as the web app.
 *
 * The route wraps the result as `{ data: DeliveryStop[] }`; apiClient unwraps
 * the outer `data`, so we normalize defensively for both shapes.
 */

import { apiClient } from './apiClient';

export type DeliveryStopStatus =
  | 'pending'
  | 'in_transit'
  | 'delivered'
  | 'failed'
  | 'skipped';

export interface DeliveryStop {
  id: number;
  delivery_route_id: number;
  order_id: number;
  order_total: number;
  customer_id: number | null;
  customer_name: string | null;
  customer_address: string | null;
  customer_phone: string | null;
  sequence: number;
  status: DeliveryStopStatus;
  scheduled_window_start: string | null;
  scheduled_window_end: string | null;
  attempted_at: string | null;
  completed_at: string | null;
  failure_reason: string | null;
  failure_notes: string | null;
  pod_signature_url: string | null;
  pod_photo_url: string | null;
  pod_received_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListMyDeliveriesFilters {
  status?: DeliveryStopStatus;
  limit?: number;
}

export async function listMyDeliveries(
  filters: ListMyDeliveriesFilters = {}
): Promise<DeliveryStop[]> {
  const raw = await apiClient.get<DeliveryStop[] | { data: DeliveryStop[] }>(
    '/customers/me/deliveries',
    filters as Record<string, any>
  );
  if (Array.isArray(raw)) return raw;
  return raw?.data ?? [];
}

/** Convenience: the delivery stop (if any) for a specific order. */
export async function getDeliveryForOrder(
  orderId: number
): Promise<DeliveryStop | null> {
  const stops = await listMyDeliveries({ limit: 100 });
  return stops.find((s) => s.order_id === orderId) ?? null;
}
